import { useState } from 'react';
import { agreementsPlatformApi } from '@/modules/agreements/services/platform-api';
import { describeUnknownAgreementFailure, requiresExplicitVerificationPolicy } from '@/modules/agreements/shared';
import { useNotification } from '@/shared/ui/notification';
import type {
  AgreementRequiredAction,
  SigningVerificationPolicy,
} from '@/api/schemas/agreements.types';

/**
 * The requirement form's state and submit.
 *
 * THE EXPLICIT-POLICY RULE
 * ------------------------
 * For a VENDOR SIGNATURE agreement (audience `tenant`, action `signature`) the operator MUST
 * state the verification policy on purpose. The API refuses an absent policy with
 * `INVALID_AGREEMENT_REQUIREMENT` rather than silently defaulting to `session_reattested`, so
 * this hook does not send an absent policy for that shape — it requires a choice.
 */
export function useRequirementForm(input: {
  documentId: string;
  versionId: string;
  audience: 'tenant' | 'customer';
  onCreated: () => void;
}) {
  const { notify } = useNotification();
  const [requiredAction, setRequiredAction] = useState<AgreementRequiredAction>('signature');
  const [policy, setPolicy] = useState<SigningVerificationPolicy | ''>('');
  const [reacceptanceRequired, setReacceptanceRequired] = useState(true);
  const [deadlineAt, setDeadlineAt] = useState('');
  const [busy, setBusy] = useState(false);

  const explicitRequired = requiresExplicitVerificationPolicy(input.audience, requiredAction);
  const policyChosen = policy !== '';
  const canSubmit = (!explicitRequired || policyChosen) && !busy;

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      await agreementsPlatformApi.createRequirement({
        documentId: input.documentId,
        versionId: input.versionId,
        audience: input.audience,
        requiredAction,
        applicability: {},
        reacceptanceRequired,
        deadlineAt: deadlineAt ? new Date(`${deadlineAt}T00:00:00.000Z`).toISOString() : null,
        gracePeriodSeconds: null,
        // Only send the policy when the operator chose one. For a vendor signature
        // agreement the API requires it; for other shapes an absent value defaults to
        // session_reattested.
        ...(policyChosen ? { verificationPolicy: policy } : {}),
      });
      notify({ tone: 'success', title: 'Requirement created' });
      input.onCreated();
    } catch (caught) {
      notify({
        tone: 'error',
        title: 'Could not create requirement',
        description: describeUnknownAgreementFailure(caught),
      });
    } finally {
      setBusy(false);
    }
  }

  return {
    requiredAction,
    setRequiredAction,
    policy,
    setPolicy,
    reacceptanceRequired,
    setReacceptanceRequired,
    deadlineAt,
    setDeadlineAt,
    busy,
    explicitRequired,
    policyChosen,
    canSubmit,
    submit,
  };
}
